import { TextStyle } from './styles';

type WrappedPart = {
  start: number;
  end: number;
  className: string;
};

/**
 * Sorts styles by text length (longest first)
 */
export function sortStylesByLength(styles: TextStyle[]): TextStyle[] {
  return [...styles].sort((a, b) => b.text.length - a.text.length);
}

/**
 * Finds all wrapped parts without overlaps
 */
export function findWrappedParts(text: string, styles: TextStyle[]): WrappedPart[] {
  const wrappedParts: WrappedPart[] = [];
  const lowerText = text.toLowerCase();

  styles.forEach(({ text: searchText, className }) => {
    const lowerSearchText = searchText.toLowerCase();
    let index = 0;
    while (index < text.length) {
      const matchIndex = lowerText.indexOf(lowerSearchText, index);
      if (matchIndex === -1) break;

      // Check if this part overlaps with already wrapped parts
      const overlaps = wrappedParts.some(
        part => matchIndex < part.end && matchIndex + searchText.length > part.start
      );

      if (!overlaps) {
        wrappedParts.push({
          start: matchIndex,
          end: matchIndex + searchText.length,
          className,
        });
      }
      index = matchIndex + 1;
    }
  });

  return wrappedParts.sort((a, b) => a.start - b.start);
}

/**
 * Builds JSX elements from text and wrapped parts
 */
export function buildElements(text: string, wrappedParts: WrappedPart[]): React.ReactNode[] {
  const elements: React.ReactNode[] = [];
  let lastIndex = 0;

  wrappedParts.forEach(({ start, end, className }, i) => {
    // Add text before the wrapped part
    if (start > lastIndex) {
      elements.push(text.slice(lastIndex, start));
    }

    // Add the wrapped part
    elements.push(
      <span key={i} className={className}>
        {text.slice(start, end)}
      </span>
    );
    lastIndex = end;
  });

  // Add remaining text
  if (lastIndex < text.length) {
    elements.push(text.slice(lastIndex));
  }

  return elements;
}
